import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Card } from '@/components/Card'
import { userManagementService, type CreateUserData } from '@/services/userManagement'
import { permissionsService } from '@/services/permissionsService'
import { Shield, FileText, User, ArrowLeft } from 'lucide-react'
import toast from 'react-hot-toast'
import type { Permission, UserRole } from '@/types'

// Função para converter permissões específicas de volta para roles
function convertPermissionsToRoles(permissions: Permission[]): UserRole[] {
  const roles: UserRole[] = []
  
  // Analisar as permissões para determinar os roles
  const permissionIds = permissions.map(p => p.id)
  
  // Verificar se tem permissões de usuário (administrador)
  if (permissionIds.some(id => 
    id.includes('user_view') || 
    id.includes('user_create') || 
    id.includes('user_edit') || 
    id.includes('user_delete')
  )) {
    roles.push('administrador')
  }
  
  // Verificar se tem permissões de vistoria
  if (permissionIds.some(id => 
    id.includes('vistoria_view') || 
    id.includes('vistoria_create') || 
    id.includes('vistoria_edit') || 
    id.includes('vistoria_delete')
  )) {
    roles.push('vistoria')
  }
  
  // Verificar se tem permissões de calendário
  if (permissionIds.some(id => 
    id.includes('calendario_view') || 
    id.includes('calendario_create') || 
    id.includes('calendario_edit') || 
    id.includes('calendario_delete')
  )) {
    roles.push('calendario')
  }
  
  // Verificar se tem permissões de cardápio
  if (permissionIds.some(id => 
    id.includes('cardapio_view') || 
    id.includes('cardapio_create') || 
    id.includes('cardapio_edit') || 
    id.includes('cardapio_delete')
  )) {
    roles.push('cardapio')
  }
  
  // Se não tem nenhum role específico, mas tem permissões básicas
  if (roles.length === 0 && permissionIds.some(id => id.includes('dashboard_view'))) {
    roles.push('usuario_basico')
  }
  
  // Se não tem nenhuma permissão específica, manter como usuário básico
  if (roles.length === 0) {
    roles.push('usuario_basico')
  }
  return roles
}

export function UserFormPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  const isEditing = !!id
  
  // Dados do usuário passados via state (fallback)
  const userDataFromState = location.state?.userData

  const [formData, setFormData] = useState<CreateUserData>({
    login: '',
    nome: '',
    senha: '',
    role: ['usuario_basico'], // Array para suportar múltiplos roles
    email: '',
    telefone: ''
  })

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Carregar dados do usuário quando estiver editando
  useEffect(() => {
    if (isEditing && id) {
      loadUserData()
    }
  }, [isEditing, id])

  const loadUserData = async () => {
    if (!id) {
      console.error('❌ ID do usuário não encontrado na URL')
      toast.error('ID do usuário não encontrado')
      navigate('/usuarios')
      return
    }

    setLoading(true)
    
    // SEMPRE carregar da API, mesmo se temos dados do state
    // O state é apenas um fallback se a API falhar
    
    try {
      const response = await userManagementService.getUserById(parseInt(id))
      
      if (response.success) {
        const user = response.data
        
        // Carregar permissões específicas da API
        try {
          const permissionsResponse = await permissionsService.getPermissionsByUser(parseInt(id))
          let userRoles = []
          
          if (permissionsResponse.success && permissionsResponse.data.length > 0) {
            // Converter permissões específicas de volta para roles
            userRoles = convertPermissionsToRoles(permissionsResponse.data)
          } else {
            // Fallback para role do usuário
            userRoles = Array.isArray(user.role) ? user.role : [user.role]
          }
          
          setFormData({
            login: user.login,
            nome: user.nome,
            senha: '',
            role: userRoles,
            email: '', // Não carregar email na edição
            telefone: '' // Não carregar telefone na edição
          })
        } catch (permError) {
          console.error('Erro ao carregar permissões da API:', permError)
          // Se falhar ao carregar permissões, usar role padrão
          setFormData({
            login: user.login,
            nome: user.nome,
            senha: '',
            role: Array.isArray(user.role) ? user.role : [user.role],
            email: '',
            telefone: ''
          })
        }
        
        toast.success('Dados do usuário carregados!')
      } else {
        console.error('❌ API retornou success=false:', response.message)
        // Fallback: usar dados do state se disponível
        if (userDataFromState && userDataFromState.id === parseInt(id)) {
          setFormData({
            login: userDataFromState.login,
            nome: userDataFromState.nome,
            senha: '',
            role: Array.isArray(userDataFromState.role) ? userDataFromState.role : [userDataFromState.role],
            email: '',
            telefone: ''
          })
          toast.success('Dados carregados do cache local')
        } else {
          toast.error(response.message || 'Erro ao carregar dados do usuário')
          navigate('/usuarios')
        }
      }
    } catch (error) {
      console.error('❌ Erro ao carregar usuário:', error)
      // Fallback: usar dados do state se disponível
      if (userDataFromState && userDataFromState.id === parseInt(id)) {
        setFormData({
          login: userDataFromState.login,
          nome: userDataFromState.nome,
          senha: '',
          role: Array.isArray(userDataFromState.role) ? userDataFromState.role : [userDataFromState.role],
          email: '',
          telefone: ''
        })
        toast.success('Dados carregados do cache local')
      } else {
        toast.error('Erro ao carregar dados do usuário')
        navigate('/usuarios')
      }
    } finally {
      setLoading(false)
    }
  }

  // Validar formulário
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.login.trim()) {
      newErrors.login = 'Login é obrigatório'
    } else if (formData.login.length < 3) {
      newErrors.login = 'Login deve ter pelo menos 3 caracteres'
    }

    if (!isEditing && !formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório'
    } else if (formData.nome && formData.nome.length < 2) {
      newErrors.nome = 'Nome deve ter pelo menos 2 caracteres'
    }

    if (!isEditing && !formData.senha) {
      newErrors.senha = 'Senha é obrigatória'
    } else if (formData.senha && formData.senha.length < 6) {
      newErrors.senha = 'Senha deve ter pelo menos 6 caracteres'
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Submeter formulário
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      if (isEditing) {
        // Salvar apenas as permissões baseadas nos roles selecionados
        const userId = parseInt(id!)
        const rolesArray = Array.isArray(formData.role) ? formData.role : [formData.role]
        
        try {
          const permissionsResponse = await permissionsService.replaceUserPermissions(userId, rolesArray)
          
          if (permissionsResponse.success) {
            toast.success(`Permissões atualizadas com sucesso! ${permissionsResponse.message}`)
          } else {
            toast.error(`Erro ao atualizar permissões: ${permissionsResponse.message}`)
          }
          
          navigate('/usuarios')
        } catch (error) {
          toast.error('Erro ao salvar permissões específicas')
        }
      } else {
        // Criar novo usuário
        const response = await userManagementService.createUser(formData)
        
        if (response.success) {
          toast.success('Usuário criado com sucesso!')
          navigate('/usuarios')
        } else {
          toast.error(response.message || 'Erro ao criar usuário')
        }
      }
    } catch (error) {
      toast.error('Erro ao processar solicitação')
      console.error('Erro:', error)
    } finally {
      setLoading(false)
    }
  }

  // Obter ícone do role
  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'administrador':
        return <Shield className="w-4 h-4" />
      case 'vistoria':
        return <FileText className="w-4 h-4" />
      case 'usuario_basico':
        return <User className="w-4 h-4" />
      default:
        return <User className="w-4 h-4" />
    }
  }

  // Obter descrição do role
  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'administrador':
        return 'Acesso completo a todas as funcionalidades do sistema'
      case 'vistoria':
        return 'Acesso a dashboard, vistorias e ajuda'
      case 'usuario_basico':
        return 'Acesso básico: dashboard e ajuda'
      default:
        return ''
    }
  }

  return (
    <div className="w-full">
      {/* Cabeçalho */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={() => navigate('/usuarios')}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title="Voltar"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isEditing ? 'Gerenciar Roles do Usuário' : 'Novo Usuário'}
            </h1>
            <p className="text-gray-600">
              {isEditing ? 'Adicione ou remova roles para alterar as permissões do usuário' : 'Preencha os dados para criar um novo usuário'}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Formulário */}
        <div className="lg:col-span-2">
          <Card className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Login */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Login *
                </label>
                <input
                  type="text"
                  value={formData.login}
                  onChange={(e) => setFormData({ ...formData, login: e.target.value })}
                  disabled={isEditing}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.login ? 'border-red-300' : 'border-gray-300'
                  } ${isEditing ? 'bg-gray-100' : ''}`}
                  placeholder="Digite o login do usuário"
                />
                {errors.login && (
                  <p className="mt-1 text-sm text-red-600">{errors.login}</p>
                )}
                {isEditing && (
                  <p className="mt-1 text-sm text-gray-500">
                    O login não pode ser alterado após a criação do usuário
                  </p>
                )}
              </div>

              {/* Nome - apenas para criação */}
              {!isEditing && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.nome ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Digite o nome completo"
                  />
                  {errors.nome && (
                    <p className="mt-1 text-sm text-red-600">{errors.nome}</p>
                  )}
                </div>
              )}

              {/* Senha - apenas para criação */}
              {!isEditing && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Senha *
                  </label>
                  <input
                    type="password"
                    value={formData.senha}
                    onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.senha ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Digite a senha"
                  />
                  {errors.senha && (
                    <p className="mt-1 text-sm text-red-600">{errors.senha}</p>
                  )}
                </div>
              )}

              {/* Email - apenas para criação */}
              {!isEditing && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.email ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Digite o email (opcional)"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
              )}

              {/* Telefone - apenas para criação */}
              {!isEditing && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    value={formData.telefone || ''}
                    onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Digite o telefone (opcional)"
                  />
                </div>
              )}

              {/* Roles */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Roles *
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'usuario_basico', label: 'Usuário Básico', description: 'Acesso básico: dashboard e ajuda' },
                    { value: 'vistoria', label: 'Vistoria', description: 'Acesso a vistorias' },
                    { value: 'calendario', label: 'Calendário', description: 'Acesso a calendários' },
                    { value: 'cardapio', label: 'Cardápio', description: 'Acesso a cardápios' },
                    { value: 'administrador', label: 'Administrador', description: 'Acesso completo' }
                  ].map((roleOption) => {
                    const isChecked = Array.isArray(formData.role) 
                      ? formData.role.includes(roleOption.value as any)
                      : formData.role === roleOption.value
                    
                    return (
                      <label key={roleOption.value} className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            const currentRoles = Array.isArray(formData.role) ? formData.role : [formData.role]
                            let newRoles: any[]
                            
                            if (e.target.checked) {
                              newRoles = [...currentRoles, roleOption.value]
                            } else {
                              newRoles = currentRoles.filter(role => role !== roleOption.value)
                            }
                            
                            // Se não há roles selecionados, adicionar usuario_basico
                            if (newRoles.length === 0) {
                              newRoles = ['usuario_basico']
                            }
                            
                            setFormData({ ...formData, role: newRoles })
                          }}
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{roleOption.label}</div>
                          <div className="text-sm text-gray-500">{roleOption.description}</div>
                        </div>
                      </label>
                    )
                  })}
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Selecione um ou mais roles. O usuário terá acesso a todas as funcionalidades dos roles selecionados.
                </p>
              </div>

              {/* Botões */}
              <div className="flex items-center justify-end space-x-4 pt-6">
                <button
                  type="button"
                  onClick={() => navigate('/usuarios')}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar Usuário'}
                </button>
              </div>
            </form>
          </Card>
        </div>

        {/* Informações sobre Roles */}
        <div className="lg:col-span-1">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Roles Disponíveis</h3>
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <User className="w-4 h-4 text-gray-600" />
                  <span className="font-medium text-gray-900">Usuário Básico</span>
                </div>
                <p className="text-sm text-gray-600">
                  Acesso básico: dashboard e ajuda
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-gray-900">Vistoria</span>
                </div>
                <p className="text-sm text-gray-600">
                  Acesso a dashboard, vistorias e ajuda
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Shield className="w-4 h-4 text-red-600" />
                  <span className="font-medium text-gray-900">Administrador</span>
                </div>
                <p className="text-sm text-gray-600">
                  Acesso completo a todas as funcionalidades
                </p>
              </div>
            </div>

            {/* Roles Selecionados */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Roles Selecionados</h4>
              {Array.isArray(formData.role) ? (
                <div className="space-y-2">
                  {formData.role.map((role, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      {getRoleIcon(role)}
                      <span className="font-medium text-blue-900 capitalize">
                        {role.replace('_', ' ')}
                      </span>
                    </div>
                  ))}
                  <p className="text-sm text-blue-700">
                    O usuário terá acesso combinado a todas essas funcionalidades.
                  </p>
                </div>
              ) : (
                <div className="flex items-center space-x-2 mb-2">
                  {getRoleIcon(formData.role)}
                  <span className="font-medium text-blue-900 capitalize">
                    {formData.role.replace('_', ' ')}
                  </span>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
